/**
 * Хозяйство против соседей — чистая логика сравнения и рабочего списка.
 *
 * Источник один: `summary.by_farm[]` и `summary.water_by_actor[]` картины
 * цикла. Сервер уже посчитал строки; здесь только доли, медианы и порядок.
 *
 * Правила (docs/research/2026-10-03-design-wave2.md, §2):
 * * доля считается от знаменателя СВОЕГО хозяйства, никогда от района;
 * * величина без значения — `null`, а не ноль («нет данных» ≠ «0 %»);
 * * ранжируются и входят в медиану только хозяйства с ≥30 обязательств:
 *   доля от трёх обязательств — шум, а не сравнение.
 */

import type { BreakdownRow } from './compare';
import { defineMessages } from '../../../i18n/define';
import { tr } from '../../../i18n/runtime';

const m = defineMessages({
  sizeXs: {
    id: 'data.farmBench.size.xs',
    defaultMessage: 'до 10 га',
    description: 'Группа хозяйств по размеру земли (сравнение хозяйств): самые малые; коротко',
  },
  sizeS: {
    id: 'data.farmBench.size.s',
    defaultMessage: '10–50 га',
    description: 'Группа хозяйств по размеру земли: от 10 до 50 гектаров; коротко',
  },
  sizeM: {
    id: 'data.farmBench.size.m',
    defaultMessage: '50–200 га',
    description: 'Группа хозяйств по размеру земли: от 50 до 200 гектаров; коротко',
  },
  sizeL: {
    id: 'data.farmBench.size.l',
    defaultMessage: 'свыше 200 га',
    description: 'Группа хозяйств по размеру земли: крупные, более 200 гектаров; коротко',
  },
  noArea: {
    id: 'data.farmBench.size.none',
    defaultMessage: 'площадь не указана',
    description: 'Группа размера, когда у хозяйства нет площади для расчёта',
  },
  farmNo: {
    id: 'data.farmBench.farmNo',
    defaultMessage: 'хозяйство №{id}',
    description: 'Название хозяйства в сравнении, когда сервер имя не назвал (строчная фраза); {id} — номер',
  },
  massifNo: {
    id: 'data.farmBench.massifNo',
    defaultMessage: 'массив №{id}',
    description: 'Название массива в сравнении, когда сервер имя не назвал (строчная фраза); {id} — номер',
  },
});


/** Порог ранжирования: меньше обязательств — доля шумит и места не получает. */
export const MIN_OBLIGATIONS = 30;

/**
 * Порог ранжирования по площади (аудит KPI 3.5, решение №645): земля хозяйства
 * под работами цикла, га. Меньше — доля площади шумит, места нет.
 */
export const MIN_AREA_HA = 10;

/**
 * Группы размера — как измерение `farm_size` каталога метрик (до 10, 10–50,
 * 50–200, свыше 200 га); здесь — по земле хозяйства под работами цикла.
 * Пороги фиксированы: одно огромное хозяйство группы не сдвигает.
 */
export const SIZE_GROUPS = [
  { id: 'xs', get label() { return tr(m.sizeXs); }, max: 10 },
  { id: 's', get label() { return tr(m.sizeS); }, max: 50 },
  { id: 'm', get label() { return tr(m.sizeM); }, max: 200 },
  { id: 'l', get label() { return tr(m.sizeL); }, max: Infinity },
] as const;
export type SizeGroup = (typeof SIZE_GROUPS)[number]['id'];

export function sizeGroupOf(areaHa: number | null): SizeGroup | null {
  if (areaHa === null) return null;
  return SIZE_GROUPS.find((g) => areaHa < g.max)?.id ?? 'l';
}

export function sizeGroupLabel(id: SizeGroup | null): string {
  return SIZE_GROUPS.find((g) => g.id === id)?.label ?? tr(m.noArea);
}

export interface FarmMetrics {
  id: number;
  name: string;
  massifId: number | null;
  massifName: string | null;
  total: number;
  reported: number;
  /** null — сервер не измерил; не ноль. */
  overdue: number | null;
  conflicts: number | null;
  contours: number;
  areaHa: number | null;
  /** Доля отчитавшихся, %; null — обязательств нет. */
  reportedShare: number | null;
  /** Доля «после срока», %; null — не измерено или обязательств нет. */
  overdueShare: number | null;
  /** Подано / запрошено, %; null — воды в журнале нет или объём неизвестен. */
  waterShare: number | null;
  requestedM3: number | null;
  deliveredM3: number | null;
  /** Хватает ли обязательств для ранжирования и медианы. */
  rankable: boolean;
  /**
   * Ход работ по площади (`area_work`, решения №645, №660): гектары работ,
   * у каждой работы вес — площадь самой работы на поле (план — площадь размещения, карантин — земля контура, полив — доля посева). `null` — площадь не
   * указана (не ноль). Доли — с сервера.
   */
  workHa: number | null;
  /** Выполнено, % площади работ хозяйства. */
  doneAreaPct: number | null;
  /** Не выполнено, га: срок наступил, отчёта «выполнено» нет («где ущерб»). */
  undoneHa: number | null;
  /**
   * После срока без отчёта — ЗЕМЛЯ (каждый гектар один раз, `overdue_area`,
   * журнал №690): сравнима с землёй хозяйства и её не превышает.
   */
  overdueHa: number | null;
  /** После срока без отчёта, га работ (сумма площадей работ) — второй слой. */
  overdueWorkHa: number | null;
  /** После срока, % площади работ хозяйства. */
  overdueAreaPct: number | null;
  /** Группа размера по земле под работами цикла. */
  sizeGroup: SizeGroup | null;
  /** Хватает ли земли (≥ MIN_AREA_HA) и есть ли площадь работ — для места по площади. */
  areaRankable: boolean;
}

export interface WaterRowLike {
  actor_id: number;
  requested_volume_m3: string | null;
  delivered_volume_m3: string | null;
}

function share(part: number | null, whole: number | null): number | null {
  if (part === null || whole === null || !whole) return null;
  return (part / whole) * 100;
}

function volume(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

export function farmMetrics(row: BreakdownRow, water: ReadonlyArray<WaterRowLike> = []): FarmMetrics {
  const o = row.totals.obligations;
  const overdue = row.totals.overdue.value;
  const wr = water.find((w) => w.actor_id === row.id);
  const requested = volume(wr?.requested_volume_m3);
  const delivered = volume(wr?.delivered_volume_m3);
  const area = row.area.total_microha;
  const aw = row.area_work ?? null;
  const awOk = !!aw && typeof aw.total_microha === 'number' && aw.total_microha > 0;
  const micro = (v: number | null | undefined): number | null => (awOk && typeof v === 'number' ? v / 1_000_000 : null);
  const areaHa = area === null || area === undefined ? null : area / 1_000_000;
  const undone = awOk && typeof aw!.due_microha === 'number' && typeof aw!.due_done_microha === 'number'
    ? Math.max(0, aw!.due_microha - aw!.due_done_microha) / 1_000_000
    : null;
  return {
    id: row.id,
    name: row.name ?? tr(m.farmNo, { id: row.id }),
    massifId: row.massif?.id ?? null,
    massifName: row.massif?.canonical_name ?? null,
    total: o.total,
    reported: o.reported,
    overdue,
    conflicts: row.totals.conflicts.value,
    contours: row.contours.total,
    areaHa,
    reportedShare: share(o.reported, o.total),
    overdueShare: share(overdue, o.total),
    waterShare: share(delivered, requested),
    requestedM3: requested,
    deliveredM3: delivered,
    rankable: o.total >= MIN_OBLIGATIONS,
    workHa: micro(aw?.total_microha),
    doneAreaPct: awOk ? aw!.done_pct : null,
    undoneHa: undone,
    overdueHa: typeof row.overdue_area?.value === 'number' ? row.overdue_area.value / 1_000_000 : null,
    overdueWorkHa: micro(aw?.overdue_microha),
    overdueAreaPct: awOk ? aw!.overdue_pct : null,
    sizeGroup: sizeGroupOf(areaHa),
    areaRankable: awOk && areaHa !== null && areaHa >= MIN_AREA_HA,
  };
}

/** Медиана; пустой ряд — `null`, а не ноль. */
export function median(values: ReadonlyArray<number>): number | null {
  if (values.length === 0) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

export type BenchKey = 'reportedShare' | 'overdueShare' | 'waterShare';

/** Значения метрики у ранжируемых хозяйств; без измерения — пропуск, не ноль. */
export function peerValues(rows: ReadonlyArray<FarmMetrics>, key: BenchKey): number[] {
  return rows.filter((r) => r.rankable).flatMap((r) => (r[key] === null ? [] : [r[key] as number]));
}

export interface Bench {
  key: BenchKey;
  /** Значение этого хозяйства; null — нет данных. */
  own: number | null;
  /** Соседи по основному массиву (ранжируемые, без самого хозяйства). */
  massif: { id: number; name: string; values: { id: number; name: string; value: number }[] } | null;
  massifMedian: number | null;
  districtMedian: number | null;
  districtCount: number;
}

/** Массив для сравнения и (если строки района его не называют) состав соседей. */
export interface PeerScope {
  massif: { id: number; name: string } | null;
  /** id хозяйств массива из картины `massif_id=`; нет — берутся строки с тем же `massifId`. */
  peerIds?: ReadonlySet<number>;
}

/**
 * Сравнение одного хозяйства по метрике. Соседи — ранжируемые хозяйства
 * основного массива; само хозяйство входит в медиану массива, если ранжируемо
 * (иначе медиана зависела бы от того, на какую карточку смотрят).
 */
export function benchmark(
  rows: ReadonlyArray<FarmMetrics>,
  farmId: number,
  key: BenchKey,
  scope?: PeerScope,
): Bench | null {
  const me = rows.find((r) => r.id === farmId);
  if (!me) return null;
  const massif =
    scope?.massif ??
    (me.massifId === null ? null : { id: me.massifId, name: me.massifName ?? tr(m.massifNo, { id: me.massifId }) });
  const inMassif = (r: FarmMetrics): boolean =>
    scope?.peerIds ? scope.peerIds.has(r.id) : massif !== null && r.massifId === massif.id;
  const peers = massif === null ? [] : rows.filter((r) => r.rankable && inMassif(r));
  const values = peers.flatMap((r) =>
    r[key] === null ? [] : [{ id: r.id, name: r.name, value: r[key] as number }],
  );
  const district = peerValues(rows, key);
  return {
    key,
    own: me[key],
    massif: massif === null ? null : { ...massif, values },
    massifMedian: median(values.map((v) => v.value)),
    districtMedian: median(district),
    districtCount: district.length,
  };
}

/** Основной массив хозяйства по реестру: максимум суммарной площади землепользований. */
export function primaryMassif(
  uses: ReadonlyArray<{ massif?: { id: number; canonical_name: string } | null; total_microha: number | null }>,
): { id: number; name: string } | null {
  const sums = new Map<number, { name: string; area: number }>();
  for (const u of uses) {
    if (!u.massif) continue;
    const hit = sums.get(u.massif.id) ?? { name: u.massif.canonical_name, area: 0 };
    hit.area += u.total_microha ?? 0;
    sums.set(u.massif.id, hit);
  }
  let best: { id: number; name: string; area: number } | null = null;
  for (const [id, v] of sums) if (!best || v.area > best.area) best = { id, ...v };
  return best ? { id: best.id, name: best.name } : null;
}

/* ------------------------------------------------------------ список */

export type SortKey =
  | 'name'
  | 'massif'
  | 'areaHa'
  | 'total'
  | 'reportedShare'
  | 'overdueShare'
  | 'conflicts'
  | 'waterShare'
  | 'workHa'
  | 'doneAreaPct'
  | 'undoneHa'
  | 'overdueHa';

export type SortDir = 'asc' | 'desc';

function sortValue(r: FarmMetrics, key: SortKey): string | number | null {
  switch (key) {
    case 'name':
      return r.name.toLowerCase();
    case 'massif':
      return r.massifName?.toLowerCase() ?? null;
    case 'workHa':
    case 'doneAreaPct':
    case 'undoneHa':
    case 'overdueHa':
      return r[key];
    default:
      return r[key];
  }
}

/** Пустые значения всегда в конце — при любом направлении: «нет данных» не лидер и не аутсайдер. */
export function sortFarms(
  rows: ReadonlyArray<FarmMetrics>,
  key: SortKey,
  dir: SortDir,
): FarmMetrics[] {
  const sign = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = sortValue(a, key);
    const y = sortValue(b, key);
    if (x === null && y === null) return a.id - b.id;
    if (x === null) return 1;
    if (y === null) return -1;
    if (x < y) return -sign;
    if (x > y) return sign;
    return a.id - b.id;
  });
}

/**
 * Место по доле «после срока» (1 = больше всего после срока) — только у
 * ранжируемых хозяйств с измеренной долей. Равные доли делят место.
 */
export function overdueRanks(rows: ReadonlyArray<FarmMetrics>): Map<number, number> {
  const ranked = rows
    .filter((r) => r.rankable && r.overdueShare !== null)
    .sort((a, b) => (b.overdueShare as number) - (a.overdueShare as number));
  const out = new Map<number, number>();
  let place = 0;
  let prev: number | null = null;
  ranked.forEach((r, i) => {
    if (r.overdueShare !== prev) {
      place = i + 1;
      prev = r.overdueShare;
    }
    out.set(r.id, place);
  });
  return out;
}

/**
 * Справедливое место (аудит KPI 3.5 «кто работает хуже», решение №645): по
 * доле площади работ ПОСЛЕ СРОКА своего хозяйства — внутри группы размера
 * (до 10 / 10–50 / 50–200 / свыше 200 га земли под циклом). 1 — больше всего
 * после срока в своей группе. Место есть только у хозяйства с землёй
 * ≥ MIN_AREA_HA и указанной площадью работ; равные доли делят место, при
 * равенстве порядок — по гектарам после срока.
 */
export function areaGroupRanks(rows: ReadonlyArray<FarmMetrics>): Map<number, { place: number; of: number; group: SizeGroup }> {
  const out = new Map<number, { place: number; of: number; group: SizeGroup }>();
  const groups = new Map<SizeGroup, FarmMetrics[]>();
  for (const r of rows) {
    if (!r.areaRankable || r.overdueAreaPct === null || r.sizeGroup === null) continue;
    const list = groups.get(r.sizeGroup) ?? [];
    list.push(r);
    groups.set(r.sizeGroup, list);
  }
  for (const [group, list] of groups) {
    list.sort((a, b) => (b.overdueAreaPct as number) - (a.overdueAreaPct as number) || (b.overdueWorkHa ?? 0) - (a.overdueWorkHa ?? 0));
    let place = 0;
    let prev: number | null = null;
    list.forEach((r, i) => {
      if (r.overdueAreaPct !== prev) {
        place = i + 1;
        prev = r.overdueAreaPct;
      }
      out.set(r.id, { place, of: list.length, group });
    });
  }
  return out;
}
